import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ArrowLeft, RefreshCw, Route } from 'lucide-react';
import DriverSettingsDrawer from './DriverSettingsDrawer';
import RoutePlanMap from './RoutePlanMap';
import RoutePlannerControlPanel from './RoutePlannerControlPanel';
import {
  API_URL,
  driverPreferencePatch,
  normalizeDriverSettings,
  selectedDriversPayload,
  todayIso,
} from './routePlannerUtils';

function AdminRoutePlanner({ setActiveComponent, userId }) {
  const [selectedDate, setSelectedDate] = useState(todayIso());
  const [config, setConfig] = useState({ zones: [], districts: [], defaults: {} });
  const [preview, setPreview] = useState(null);
  const [driverSettings, setDriverSettings] = useState({});
  const [plan, setPlan] = useState(null);
  const [strategy, setStrategy] = useState('use_all_available');
  const [serviceMinutes, setServiceMinutes] = useState(6);
  const [maxPackagesDefault, setMaxPackagesDefault] = useState(25);
  const [useGoogle, setUseGoogle] = useState(true);
  const [driversOpen, setDriversOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [moveDrafts, setMoveDrafts] = useState({});
  const [mapDrawingMode, setMapDrawingMode] = useState(false);
  const [mapSelection, setMapSelection] = useState([]);

  const fetchConfig = useCallback(async () => {
    const { data } = await axios.get(`${API_URL}/api/admin/route-planner/config`);
    setConfig(data);
    setDriverSettings(normalizeDriverSettings(data.drivers || []));
    setServiceMinutes(data.defaults?.service_minutes || 6);
    setMaxPackagesDefault(data.defaults?.max_packages || 25);
  }, []);

  const fetchPreview = useCallback(async () => {
    const { data } = await axios.get(`${API_URL}/api/admin/route-planner/preview`, {
      params: { date: selectedDate },
    });
    setPreview(data);
  }, [selectedDate]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  const selectedCount = useMemo(
    () => Object.values(driverSettings).filter((driver) => driver.selected).length,
    [driverSettings]
  );
  const availableDrivers = useMemo(() => Object.values(driverSettings), [driverSettings]);

  function updateDriver(userIdKey, patch) {
    setDriverSettings((current) => ({
      ...current,
      [userIdKey]: { ...current[userIdKey], ...patch },
    }));
  }

  async function saveDriverPreferences(driver) {
    await axios.patch(
      `${API_URL}/api/admin/route-planner/drivers/${driver.driver_id}`,
      driverPreferencePatch(driver, maxPackagesDefault)
    );
    setMessage(`Preferencias guardadas para ${driver.name}`);
  }

  async function createZone(payload) {
    await axios.post(`${API_URL}/api/admin/route-planner/zones`, payload);
    setMessage(`Zona ${payload.name} creada`);
    await fetchConfig();
  }

  async function saveZone(zone, patch) {
    const zoneId = zone.slug || zone.id;
    await axios.patch(`${API_URL}/api/admin/route-planner/zones/${zoneId}`, patch);
    setMessage(`Zona ${zone.name || zone.label} actualizada`);
    await fetchConfig();
  }

  async function togglePriority(delivery) {
    await axios.patch(`${API_URL}/api/admin/route-planner/deliveries/${delivery.id}/priority`, {
      priority: !delivery.priority,
      priority_level: !delivery.priority ? 1 : 0,
    });
    await fetchPreview();
  }

  async function markPostponedAsPriority() {
    const { data } = await axios.patch(`${API_URL}/api/admin/route-planner/deliveries/bulk-priority`, {
      delivery_date: selectedDate,
      status: 'postergado',
      priority: true,
      priority_level: 1,
    });
    setMessage(`${data.updated_count || 0} postergados marcados como prioridad`);
    await fetchPreview();
  }

  async function optimizePlan() {
    setLoading(true);
    setMessage('');
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/route-plans/optimize`, {
        delivery_date: selectedDate,
        admin_user_id: userId,
        strategy,
        service_minutes: Number(serviceMinutes || 6),
        max_packages_default: Number(maxPackagesDefault || 25),
        use_google: useGoogle,
        drivers: selectedDriversPayload(driverSettings, maxPackagesDefault),
      });
      setPlan(data);
      setMessage(`Plan creado: ${data.summary?.assigned_stops || 0} entregas asignadas`);
    } catch (err) {
      setMessage(err.response?.data?.error || 'No se pudo optimizar la ruta');
    } finally {
      setLoading(false);
    }
  }

  async function loadLatestPlan() {
    if (!preview?.latest_plan?.id) return;
    const { data } = await axios.get(`${API_URL}/api/admin/route-plans/${preview.latest_plan.id}`);
    setPlan(data);
  }

  async function refreshPlan() {
    if (!plan?.id) return;
    const { data } = await axios.get(`${API_URL}/api/admin/route-plans/${plan.id}`);
    setPlan(data);
  }

  async function moveStop(stop, currentRouteId, delta) {
    await axios.post(`${API_URL}/api/admin/route-plans/${plan.id}/move-stop`, {
      stop_id: stop.id,
      target_route_id: currentRouteId,
      target_sequence: Math.max(1, stop.sequence + delta),
    });
    await refreshPlan();
  }

  async function moveStopToRoute(stop, fallbackDestination) {
    const draft = moveDrafts[stop.id];
    const destination = draft?.target_destination || (draft?.target_route_id ? `route:${draft.target_route_id}` : null) || fallbackDestination;
    if (!destination) return;
    await moveStopsToDestination([stop.id], destination, draft.target_sequence);
  }

  async function moveStopsToDestination(stopIds, destination, targetSequence) {
    const [kind, rawId] = String(destination || '').split(':');
    const payload = {
      stop_ids: stopIds,
      target_sequence: Number(targetSequence || 9999),
    };
    if (kind === 'driver') {
      payload.target_driver_id = Number(rawId);
    } else {
      payload.target_route_id = Number(rawId);
    }
    const { data } = await axios.post(`${API_URL}/api/admin/route-plans/${plan.id}/move-stop`, payload);
    setPlan(data);
    return data;
  }

  async function assignUnassignedDelivery(deliveryPointId, destination, targetSequence) {
    const [kind, rawId] = String(destination || '').split(':');
    const payload = {
      delivery_point_id: deliveryPointId,
      target_sequence: Number(targetSequence || 9999),
    };
    if (kind === 'driver') {
      payload.target_driver_id = Number(rawId);
    } else {
      payload.target_route_id = Number(rawId);
    }
    const { data } = await axios.post(`${API_URL}/api/admin/route-plans/${plan.id}/assign-unassigned`, payload);
    setPlan(data);
    setMessage(`Entrega #${deliveryPointId} asignada`);
    return data;
  }

  async function resequenceRoute(route) {
    const { data } = await axios.post(`${API_URL}/api/admin/route-plans/${plan.id}/routes/${route.id}/resequence`);
    setPlan(data);
    setMessage(`Ruta de ${route.driver_name || `Driver ${route.driver_id}`} reordenada`);
    return data;
  }

  function handleMapSelection(items) {
    setMapDrawingMode(false);
    if (!items?.length) {
      setMessage('No se encontraron entregas dentro del area seleccionada');
      return;
    }
    setMapSelection(items);
  }

  async function assignMapSelection(destination, targetSequence) {
    const [kind, rawId] = String(destination || '').split(':');
    const payload = {
      items: mapSelection.map((item) => ({ type: item.type, id: item.id })),
      target_sequence: Number(targetSequence || 9999),
    };
    if (kind === 'driver') {
      payload.target_driver_id = Number(rawId);
    } else {
      payload.target_route_id = Number(rawId);
    }
    const { data } = await axios.post(`${API_URL}/api/admin/route-plans/${plan.id}/map-assign`, payload);
    setPlan(data);
    setMessage(`${mapSelection.length} entregas movidas desde el mapa`);
    setMapSelection([]);
  }

  async function publishPlan() {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/route-plans/${plan.id}/publish`);
      setPlan(data);
      setMessage('Rutas publicadas para conductores');
      await fetchPreview();
    } catch (err) {
      setMessage(err.response?.data?.error || 'No se pudo publicar');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header
        loading={loading}
        selectedCount={selectedCount}
        optimizePlan={optimizePlan}
        setActiveComponent={setActiveComponent}
      />

      <div className="grid grid-cols-1 gap-3 p-3 xl:h-[calc(100vh-82px)] xl:grid-cols-[430px_minmax(0,1fr)] xl:overflow-hidden">
        <RoutePlannerControlPanel
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          strategy={strategy}
          setStrategy={setStrategy}
          serviceMinutes={serviceMinutes}
          setServiceMinutes={setServiceMinutes}
          maxPackagesDefault={maxPackagesDefault}
          setMaxPackagesDefault={setMaxPackagesDefault}
          useGoogle={useGoogle}
          setUseGoogle={setUseGoogle}
          preview={preview}
          selectedCount={selectedCount}
          availableDrivers={availableDrivers}
          onOpenDrivers={() => setDriversOpen(true)}
          onLoadLatestPlan={loadLatestPlan}
          plan={plan}
          loading={loading}
          publishPlan={publishPlan}
          moveStop={moveStop}
          moveStopToRoute={moveStopToRoute}
          moveStopsToDestination={moveStopsToDestination}
          onResequenceRoute={resequenceRoute}
          moveDrafts={moveDrafts}
          setMoveDrafts={setMoveDrafts}
          onTogglePriority={togglePriority}
          onMarkPostponedPriority={markPostponedAsPriority}
          onAssignUnassigned={assignUnassignedDelivery}
        />

        <main className="flex min-h-[620px] min-w-0 flex-col gap-3 xl:min-h-0">
          {message && (
            <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700">
              {message}
            </div>
          )}
          <RoutePlanMap
            plan={plan}
            preview={preview}
            drawingMode={mapDrawingMode}
            onToggleDrawing={() => setMapDrawingMode((current) => !current)}
            onMapSelection={handleMapSelection}
            className="min-h-[620px] flex-1 xl:min-h-0"
          />
        </main>
      </div>

      <DriverSettingsDrawer
        open={driversOpen}
        onClose={() => setDriversOpen(false)}
        driverSettings={driverSettings}
        updateDriver={updateDriver}
        saveDriverPreferences={saveDriverPreferences}
        createZone={createZone}
        saveZone={saveZone}
        zones={config.zones || []}
        districts={config.districts || []}
      />

      {!!mapSelection.length && (
        <MapSelectionAssignModal
          items={mapSelection}
          plan={plan}
          availableDrivers={availableDrivers}
          onAssign={assignMapSelection}
          onClose={() => setMapSelection([])}
        />
      )}
    </div>
  );
}

function buildDestinationGroups(plan, availableDrivers) {
  const routes = plan?.routes || [];
  const routeDriverIds = new Set(routes.map((route) => Number(route.driver_id)).filter(Boolean));
  const routeOptions = routes.map((route) => ({
    value: `route:${route.id}`,
    label: route.driver_name || `Driver ${route.driver_id}`,
  }));
  const driverOptions = availableDrivers
    .filter((driver) => driver.user_id && !routeDriverIds.has(Number(driver.user_id)))
    .map((driver) => ({
      value: `driver:${driver.user_id}`,
      label: `${driver.name || `Driver ${driver.user_id}`} (nuevo)`,
    }));

  return [
    { label: 'Rutas del plan', options: routeOptions },
    { label: 'Conductores disponibles', options: driverOptions },
  ].filter((group) => group.options.length);
}

function MapSelectionAssignModal({ items, plan, availableDrivers, onAssign, onClose }) {
  const destinationGroups = useMemo(() => buildDestinationGroups(plan, availableDrivers), [availableDrivers, plan]);
  const defaultDestination = destinationGroups[0]?.options?.[0]?.value || '';
  const [destination, setDestination] = useState(defaultDestination);
  const [sequence, setSequence] = useState('');
  const [saving, setSaving] = useState(false);
  const assignedCount = items.filter((item) => item.type === 'stop').length;
  const unassignedCount = items.filter((item) => item.type === 'unassigned').length;

  useEffect(() => {
    setDestination((current) => current || defaultDestination);
  }, [defaultDestination]);

  async function submit() {
    const target = destination || defaultDestination;
    if (!target) return;
    setSaving(true);
    try {
      await onAssign(target, sequence);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[1500] flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 p-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Asignar seleccion del mapa</h3>
            <p className="text-sm text-slate-500">
              {items.length} entregas seleccionadas | {assignedCount} en ruta | {unassignedCount} sin asignar
            </p>
          </div>
          <button onClick={onClose} className="rounded-lg px-2 py-1 text-sm font-bold text-slate-500 hover:bg-slate-100">
            Cerrar
          </button>
        </div>

        <div className="space-y-3 p-4">
          <label className="block text-xs font-semibold text-slate-600">
            Conductor / ruta destino
            <select
              value={destination || defaultDestination}
              onChange={(event) => setDestination(event.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            >
              {destinationGroups.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>

          <label className="block text-xs font-semibold text-slate-600">
            Insertar desde secuencia
            <input
              type="number"
              min="1"
              value={sequence}
              onChange={(event) => setSequence(event.target.value)}
              placeholder="Vacio = al final"
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </label>

          <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200">
            {items.map((item) => (
              <div key={`${item.type}-${item.id}`} className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2 text-sm last:border-b-0">
                <span className="font-semibold text-slate-800">{item.label}</span>
                <span className="truncate text-xs text-slate-500">
                  {item.type === 'unassigned' ? 'Sin asignar' : item.driver_name || 'En ruta'} | {item.district || 'Sin distrito'}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4">
          <button onClick={onClose} className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold text-slate-600">
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={saving || !defaultDestination}
            className="rounded-lg bg-brand-primary px-3 py-2 text-sm font-bold text-white disabled:opacity-50"
          >
            {saving ? 'Asignando...' : 'Asignar seleccion'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Header({ loading, selectedCount, optimizePlan, setActiveComponent }) {
  return (
    <div className="sticky top-0 z-[1200] border-b border-slate-200 bg-white">
      <div className="px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            onClick={() => setActiveComponent('OptionsAdmin')}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </button>
          <div className="flex items-center gap-2">
            <Route className="h-6 w-6 text-brand-primary" />
            <div>
              <h1 className="text-xl font-bold text-slate-900">Planificador de rutas</h1>
              <p className="text-sm text-slate-500">Asignacion masiva para Lima</p>
            </div>
          </div>
          <button
            onClick={optimizePlan}
            disabled={loading || selectedCount === 0}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-primary px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Optimizar
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminRoutePlanner;
